import { useEffect, useState } from 'react';
import TileCard from '../TileCard';
import { getCachedResponse, getWorkflowDurations } from '../../api/client';
import type { Env, WorkflowDurations } from '../../types';

const HELP =
  'Per-workflow duration percentiles over the last 24h, sorted by p95 descending. ' +
  'p50 is typical, p95 shows the tail that slow users feel, p99/max flag outliers ' +
  'worth investigating in App Insights transaction search. Rows where p95 > 5s are ' +
  'highlighted amber; > 15s red.';

function formatMs(ms: number): string {
  if (!ms || ms < 0) return '—';
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  return `${(ms / 60_000).toFixed(1)} min`;
}

function durationClass(p95: number): string {
  if (p95 > 15_000) return 'bad';
  if (p95 > 5000) return 'warn';
  return '';
}

function formatWindow(hours: number): string {
  if (hours < 24) return `${hours}h`;
  if (hours === 24) return '24h';
  if (hours % 24 === 0) return `${hours / 24}d`;
  return `${hours}h`;
}

interface Props {
  env: Env;
  windowHours?: number;
  refreshToken?: number;
}

export default function WorkflowDurationsTile({ env, windowHours = 24, refreshToken = 0 }: Props) {
  const [data, setData] = useState<WorkflowDurations | null>(() =>
    getCachedResponse<WorkflowDurations>('workflow-durations', { env, windowHours }),
  );
  const [loading, setLoading] = useState(data === null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const cached = getCachedResponse<WorkflowDurations>('workflow-durations', { env, windowHours });
    if (cached) setData(cached);
    setLoading(cached === null);
    setError(null);
    getWorkflowDurations(env, windowHours)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [env, windowHours, refreshToken]);

  const rows = data?.rows ?? [];
  const generatedFooter = data ? (
    <span>As of {new Date(data.generatedAt).toLocaleTimeString()} · {formatWindow(data.windowHours)} window · top 50 by p95</span>
  ) : undefined;

  return (
    <TileCard
      title={`Workflow durations — last ${formatWindow(windowHours)} (${env.toUpperCase()})`}
      help={HELP}
      loading={loading}
      error={error}
      footer={generatedFooter}
    >
      {data?.status === 'not-configured' && <p className="tile-empty">{data.message}</p>}
      {data?.status === 'error' && <div className="tile-error">Query failed: {data.message}</div>}
      {data?.status === 'ok' && rows.length === 0 && (
        <p className="tile-empty">No workflow runs found in the last 24 hours.</p>
      )}
      {data?.status === 'ok' && rows.length > 0 && (
        <div className="workflow-table-wrap">
          <table className="workflow-table">
            <thead>
              <tr>
                <th>Workflow</th>
                <th className="num">Runs</th>
                <th className="num">p50</th>
                <th className="num">p95</th>
                <th className="num">p99</th>
                <th className="num">max</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.workflow}>
                  <td>{r.workflow}</td>
                  <td className="num">{r.total}</td>
                  <td className="num">{formatMs(r.p50Ms)}</td>
                  <td className={`num ${durationClass(r.p95Ms)}`}>{formatMs(r.p95Ms)}</td>
                  <td className="num">{formatMs(r.p99Ms)}</td>
                  <td className="num">{formatMs(r.maxMs)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </TileCard>
  );
}
